/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {AllDrawersState} from 'shared/Drawers';

import CenteredSpinner from './CenteredSpinner';
import FileTreeSidebar from './FileTreeSidebar';
import {useCommand} from './KeyboardShortcuts';
import PullRequest from './PullRequest';
import PullRequestHeader from './PullRequestHeader';
import PullRequestTimeline from './PullRequestTimeline';
import PullRequestTimelineCommentInput from './PullRequestTimelineCommentInput';
import {APP_HEADER_HEIGHT} from './constants';
import {gitHubOrgAndRepoAtom, gitHubPullRequestIDAtom} from './jotai';
import {Box, Text} from '@primer/react';
import {atom, useSetAtom} from 'jotai';
import React, {Component, Suspense, useEffect} from 'react';
import {Drawers} from 'shared/Drawers';

import './PullRequestLayout.css';

const HEADER_HEIGHT = 100;
const TOTAL_HEADER_HEIGHT = HEADER_HEIGHT + APP_HEADER_HEIGHT;
const COMMENT_INPUT_HEIGHT = 125;

const DRAWER_STORAGE_KEY = 'reviewstack:drawerState';

const DEFAULT_DRAWER_STATE: AllDrawersState = {
  right: {size: 500, collapsed: false},
  left: {size: 300, collapsed: true},
  top: {size: 200, collapsed: true},
  bottom: {size: 200, collapsed: true},
};

function loadDrawerState(): AllDrawersState {
  try {
    const stored = localStorage.getItem(DRAWER_STORAGE_KEY);
    if (stored != null) {
      const parsed = JSON.parse(stored) as Partial<AllDrawersState>;
      return {...DEFAULT_DRAWER_STATE, ...parsed};
    }
  } catch {
    // Ignore corrupt data
  }
  return DEFAULT_DRAWER_STATE;
}

const baseDrawerStateAtom = atom<AllDrawersState>(loadDrawerState());

/** Wraps the base atom to persist changes to localStorage. */
const drawerStateAtom = atom(
  get => get(baseDrawerStateAtom),
  (_get, set, update: AllDrawersState | ((prev: AllDrawersState) => AllDrawersState)) => {
    set(baseDrawerStateAtom, prev => {
      const next = typeof update === 'function' ? update(prev) : update;
      try {
        localStorage.setItem(DRAWER_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Quota exceeded or similar — ignore
      }
      return next;
    });
  },
);

export default function PullRequestLayout({
  org,
  repo,
  number,
}: {
  org: string;
  repo: string;
  number: number;
}): React.ReactElement {
  const setOrgAndRepo = useSetAtom(gitHubOrgAndRepoAtom);
  const setPullRequestID = useSetAtom(gitHubPullRequestIDAtom);

  useEffect(() => {
    setOrgAndRepo({org, repo});
  }, [org, repo, setOrgAndRepo]);

  useEffect(() => {
    setPullRequestID(number);
  }, [number, setPullRequestID]);

  const setDrawerState = useSetAtom(drawerStateAtom);
  useCommand('ToggleSidebar', () => {
    setDrawerState(state => ({
      ...state,
      right: {...state.right, collapsed: !state.right.collapsed},
    }));
  });
  useCommand('ToggleFileTree', () => {
    setDrawerState(state => ({
      ...state,
      left: {...state.left, collapsed: !state.left.collapsed},
    }));
  });

  return (
    <Box>
      <PullRequestHeader height={HEADER_HEIGHT} />
      <Suspense fallback={<CenteredSpinner message="Loading pull request..." />}>
        <Drawers
          drawerState={drawerStateAtom}
          errorBoundary={ErrorBoundary}
          leftLabel={<Text className="drawer-label-text">...</Text>}
          left={<FileTreeSidebar />}
          rightLabel={<Text className="drawer-label-text">...</Text>}
          right={<TimelineDrawer />}>
          <Box display="flex" flexDirection="row" width="100%">
            <Box height={`calc(100vh - ${TOTAL_HEADER_HEIGHT}px)`} overflow="auto" flex={1} minWidth={0}>
              <PullRequest />
            </Box>
          </Box>
        </Drawers>
      </Suspense>
    </Box>
  );
}

function TimelineDrawer() {
  return (
    <Box display="flex" flexDirection="column" height={`calc(100vh - ${TOTAL_HEADER_HEIGHT}px)`}>
      <Box height={`calc(100% - ${COMMENT_INPUT_HEIGHT}px)`} overflow="auto">
        <PullRequestTimeline />
      </Box>
      <Box display="flex" height={COMMENT_INPUT_HEIGHT}>
        <PullRequestTimelineCommentInput />
      </Box>
    </Box>
  );
}

type Props = {
  children: React.ReactNode;
};

type State = {error: Error | null};

class ErrorBoundary extends Component<Props, State> {
  static getDerivedStateFromError(error: Error) {
    return {error};
  }

  render() {
    return this.props.children;
  }
}
